import { type AxiosError } from 'axios';

type ErrorResponse = { message?: string };

/** Password forms retain only safe response details, never the Axios request containing credentials. */
export class PasswordUpdateError extends Error {
  readonly recoveryRequired: boolean;
  readonly reference?: string;

  constructor(error: AxiosError<{ message?: unknown; code?: unknown; reference?: unknown }>) {
    const data = error.response?.data;
    const recoveryRequired =
      data?.code === 'PASSWORD_RECOVERY_REQUIRED' ||
      !error.response ||
      error.response.status >= 500;
    const reference =
      typeof data?.reference === 'string' &&
      /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(data.reference)
        ? data.reference
        : undefined;
    super(
      recoveryRequired
        ? 'Password update could not be confirmed. Check sign-in or contact Allume support before trying again.' +
            (reference ? ` Reference: ${reference}.` : '')
        : typeof data?.message === 'string' && data.message.length <= 600
        ? data.message
        : 'Password update was rejected. Check your details and try again.',
    );
    this.name = 'PasswordUpdateError';
    this.recoveryRequired = recoveryRequired;
    this.reference = reference;
  }
}

export const resolveHttpErrorMessage = (error: unknown, defaultMessage: string): string => {
  if (typeof error === 'string') {
    return error;
  }

  // Axios error: more useful error message for the user
  if (typeof error === 'object' && error !== null && 'response' in error) {
    const axiosError = error as AxiosError<ErrorResponse>;
    return axiosError.response?.data?.message || axiosError.message || defaultMessage;
  }

  // Technical error message + fallback default message
  const errorObj = error as Error;
  return errorObj?.message || defaultMessage;
};
